import { splitEncoding } from '@contentful/live-preview';
import { parse } from 'cookie';

import { getLocaleFromParams, hasMultipleLocales } from '@/utils/locale';
import { resolveLocale } from '@/utils/resolveLocale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import {
  fetchCmsPageDataWithExtraData,
  fetchSelectorModalByMarketingPartner,
  HOME_SLUG,
} from '@/integrations/contentful';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { captureMessage } from '@/lib/errors';
import { isSupportedLocale } from '@/lib/locales/locales';
import { getGqlRegionFromLocale, getRegionFromLocale } from '@/lib/regions';
import { CacheHeaderType, setCacheHeader } from '@/lib/revalidate';
import { CmsView } from '@/views/CmsView';

import { findMarketingPartnerCard } from '../../utils/findMarketingPartnerCard';

export const getServerSideProps = commonServerSideProps(async (context) => {
  const routerLocale = getLocaleFromParams(context?.params);

  // There is a chance that dynamic router is taken as public file path (see `isPublicFileRequest`),
  // e.g. `/sunglasses/JZ000001.23`
  // Need to prepend the default locale to file-like URLs that don't start with a supported locale.
  if (!isSupportedLocale(routerLocale)) {
    const cookies = parse(context.req.headers.cookie || '');

    const locale = resolveLocale({
      cookies,
    });

    return {
      redirect: {
        destination: `/${locale}${context.resolvedUrl}`,
        permanent: false,
      },
    };
  }

  const { channel, languageCode } = getGqlRegionFromLocale(routerLocale);
  const { country, locale } = getRegionFromLocale(routerLocale);
  const contentfulLocale = locale.contentful ?? locale.locale;
  const slugArr = (context.params?.slug || [HOME_SLUG]) as string[];
  const slug = slugArr.join(HOME_SLUG);

  const countryCode = country.slug;
  const draftMode = resolveContentDraftMode(context);

  const {
    breadcrumb,
    cmsData,
    cmsDefaultLayoutData,
    cmsExtraData,
    pageType,
    saleorProductSkus,
    saleorProductSkusProductVariant,
    seoConfigBuilder,
  } = await fetchCmsPageDataWithExtraData(
    slug,
    contentfulLocale,
    languageCode,
    channel,
    countryCode,
    draftMode,
  );

  if (context.req.url && hasMultipleLocales(context.req.url)) {
    captureMessage('[URL parsing error] contains multiple locales', {
      extra: {
        requestUrl: extractPathFromUrl(context.req.url),
        referer: context.req.headers.referer ?? '',
      },
    });
  }

  if (!cmsData || cmsData.length === 0) {
    return {
      notFound: true,
    };
  }

  if (pageType === 'News') {
    // Remove leading slash from slug to avoid double slashes in URL
    const cleanSlug = slug.startsWith('/') ? slug.slice(1) : slug;
    const newsUrl = `/${routerLocale}/news/${cleanSlug}`;
    return {
      redirect: {
        destination: newsUrl,
        permanent: false,
      },
    };
  }

  if (slug === HOME_SLUG) {
    setCacheHeader(CacheHeaderType.HOME_PAGE, context);
  } else {
    setCacheHeader(CacheHeaderType.CMS_PAGE, context);
  }

  const marketingPartnerCard = findMarketingPartnerCard(cmsData);

  const marketingPartnerName = splitEncoding(
    (marketingPartnerCard as { url?: string })?.url ?? '',
  ).cleaned;

  const marketingSelectorModalResult =
    await fetchSelectorModalByMarketingPartner(
      marketingPartnerName,
      draftMode,
      contentfulLocale,
    );

  //TODO mainHeader overwrite
  return {
    props: {
      cmsData: cmsData,
      cmsExtraDataRecord: cmsExtraData,
      cmsDefaultLayoutData,
      breadcrumb,
      seoConfig: seoConfigBuilder?.build(),
      productVariant: saleorProductSkusProductVariant,
      skuArray: saleorProductSkus ?? '',
      slug,
      locale,
      country,
      marketingSelectorModalResult,
      draftMode,
      // Forwarded by CmsView.getLayout to the Recently Viewed gate: this route
      // serves Editorial, Legal and News alike.
      pageType: pageType ?? null,
    },
  };
});

export default CmsView;
